/**
 * Initialize all performance optimizations
 * Call this once in App.tsx
 */

export function initPerformanceOptimizations() {
  // 1. Lazy load images below the fold
  if (typeof window !== 'undefined') {
    initLazyLoading();
    
    // Re-init after dynamic content loads
    const observer = new MutationObserver(() => {
      initLazyLoading();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // 2. Preload critical images (logos, icons, etc.)
  preloadCriticalImages([
    '/logo.svg',
    '/icon-192.png',
    '/icon-512.png',
  ]);

  // 3. Prefetch DNS for external resources
  if (typeof document !== 'undefined') {
    const domains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
    ];

    domains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = domain;
      document.head.appendChild(link);
    });
  }

  // 4. Enable passive event listeners for better scroll performance
  if (typeof window !== 'undefined') {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function(
      type: string,
      listener: any,
      options?: any
    ) {
      if (type === 'touchstart' || type === 'touchmove' || type === 'wheel') {
        if (typeof options === 'object') {
          options.passive = options.passive ?? true;
        } else {
          options = { passive: true };
        }
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  }
}

/**
 * Lazy load images using Intersection Observer
 * Usage: <img data-src="image.jpg" className="lazy" />
 */
export function initLazyLoading() {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.getAttribute('data-src');
          if (src) {
            img.src = src;
            img.classList.remove('lazy');
            imageObserver.unobserve(img);
          }
        }
      });
    }, {
      rootMargin: '50px' // Start loading 50px before entering viewport
    });

    document.querySelectorAll('img.lazy').forEach(img => {
      imageObserver.observe(img);
    });
  }
}

/**
 * Preload critical images during idle time
 */
export function preloadCriticalImages(urls: string[]) {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => {
      urls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        link.as = 'image';
        document.head.appendChild(link);
      });
    });
  }
}

/**
 * Convert image to WebP if supported
 */
export function getOptimizedImageUrl(url: string): string {
  // Check WebP support
  const supportsWebP = document.createElement('canvas')
    .toDataURL('image/webp')
    .startsWith('data:image/webp');

  if (supportsWebP && !url.includes('.svg')) {
    // Replace extension with .webp
    return url.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  }

  return url;
}
